import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {FieldWrapper, IRange, RangeInput} from "@pg-design/inputs-module";
import {SystemModal} from "@pg-design/modal-module";
import {CheckboxControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {hitUserSegmentRooms} from "@pg-mono/google-tag-manager";
import {ControllerRHF, FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import {FILTERS_TEST_IDS, SEARCH_FILTERS_AND_SORT_TEST_ID} from "@pg-mono/test-ids";
import {OfferType} from "@pg-rp/common-types";

import {IOfferModalState} from "../../offer/types/OfferModalState";
import {errorMessages, hasAreaValidationError} from "../../real_estate/utils/has_area_validation_error";
import {SearchFiltersRooms} from "../../search/components/filters/SearchFiltersRooms";
import {getFilterAreaTestId} from "../../search/utils/filter_area_test_id";
import {getAreaFilterLimitedValues} from "../../search/utils/get_area_filter_proper_value";
import {useUserSearchPreferences} from "../../user/hooks/use_user_search_preferences";

interface IProps {
    offerType: OfferType;
    isOpen: boolean;
    filters?: IOfferModalState["filters"];
    handleClose: (values?: IFiltersFormValues) => void;
    handleConfirm: (values: IFiltersFormValues) => void;
}

export interface IFiltersFormValues {
    rooms: IOfferModalState["filters"]["rooms"];
    area: IOfferModalState["filters"]["area"];
    floorGround: boolean;
    floorGroundWithGarden: boolean;
    floorOneToFour: boolean;
    floorFivePlus: boolean;
    floorLast: boolean;
    floorLastWithTerrace: boolean;
}

const castToNumberIfExists = (value: string | undefined) => {
    return value ? Number.parseInt(value) : "";
};

export function PropertyListFiltersModal(props: IProps) {
    const {isOpen, filters, handleClose, handleConfirm, offerType} = props;
    const {userPreferences, saveUserPreferences} = useUserSearchPreferences();
    const userPreferencesFilters = userPreferences?.data?.filters || {};

    const formHook = useFormRHF<IFiltersFormValues>({
        defaultValues: {
            rooms: filters
                ? filters.rooms
                : {lower: castToNumberIfExists(userPreferencesFilters.rooms_0), upper: castToNumberIfExists(userPreferencesFilters.rooms_1)},
            area: filters ? filters.area : {lower: userPreferencesFilters.area_0 || "", upper: userPreferencesFilters.area_1 || ""},
            floorGround: filters ? Boolean(filters.floor_choices?.includes(1)) : Boolean(userPreferencesFilters.floor_choices?.includes("1")),
            floorGroundWithGarden: filters ? Boolean(filters.floor_choices?.includes(2)) : Boolean(userPreferencesFilters.floor_choices?.includes("2")),
            floorOneToFour: filters ? Boolean(filters.floor_choices?.includes(3)) : Boolean(userPreferencesFilters.floor_choices?.includes("3")),
            floorFivePlus: filters ? Boolean(filters.floor_choices?.includes(4)) : Boolean(userPreferencesFilters.floor_choices?.includes("4")),
            floorLast: filters ? Boolean(filters.floor_choices?.includes(5)) : Boolean(userPreferencesFilters.floor_choices?.includes("5")),
            floorLastWithTerrace: filters ? Boolean(filters.floor_choices?.includes(6)) : Boolean(userPreferencesFilters.floor_choices?.includes("6"))
        }
    });

    const {handleSubmit, control} = formHook;

    const handlers = {
        onSubmit: (data: IFiltersFormValues) => {
            if (hasAreaValidationError(data.area)) {
                return;
            }

            hitUserSegmentRooms(data.rooms);

            saveUserPreferences({
                filters: {
                    rooms_0: data.rooms.lower?.toString() || undefined,
                    rooms_1: data.rooms.upper?.toString() || undefined,
                    area_0: data.area.lower?.toString() || undefined,
                    area_1: data.area.upper?.toString() || undefined,
                    floor_choices: [
                        data.floorGround ? "1" : "",
                        data.floorGroundWithGarden ? "2" : "",
                        data.floorOneToFour ? "3" : "",
                        data.floorFivePlus ? "4" : "",
                        data.floorLast ? "5" : "",
                        data.floorLastWithTerrace ? "6" : ""
                    ].filter(Boolean)
                }
            });
            handleConfirm(data);
        },
        onCancel: () => {
            handleClose(formHook.getValues());
        }
    };

    const areaErrorMessage = hasAreaValidationError(formHook.watch("area")) ? errorMessages.area : undefined;

    return (
        <SystemModal isOpen={isOpen} variant="fit" headerVariant="gray" onModalClose={() => handleClose?.()}>
            <SystemModal.Header>
                <Text variant="body_copy_2">Filtry</Text>
            </SystemModal.Header>
            <SystemModal.Content className={modalContent}>
                <form onSubmit={handleSubmit(handlers.onSubmit)} className={formStyle}>
                    <FieldWrapper
                        labelContent={
                            <Text variant="body_copy_1" color="var(--colors-gray-700)" mb={1}>
                                Pokoje
                            </Text>
                        }
                        className={css({mb: 3})}
                        dataTestId={getFilterAreaTestId("rooms")}
                    >
                        <ControllerRHF
                            control={control}
                            name="rooms"
                            render={({field}) => (
                                <SearchFiltersRooms
                                    {...field}
                                    className={css({mb: 5})}
                                    toggleDropdown={() => null}
                                    clearField={() => null}
                                    showFooter={false}
                                    onChange={(_, value) => {
                                        field.onChange(value);
                                    }}
                                />
                            )}
                        />
                    </FieldWrapper>
                    <FieldWrapper
                        labelContent={
                            <Text variant="body_copy_1" color="var(--colors-gray-700)" mb={1}>
                                Metraż
                            </Text>
                        }
                        className={css({mb: 3})}
                        dataTestId={getFilterAreaTestId("area")}
                    >
                        <ControllerRHF
                            control={control}
                            name="area"
                            render={({field}) => (
                                <RangeInput
                                    {...field}
                                    error={areaErrorMessage}
                                    onChange={(_, value) => {
                                        const limitedValues = getAreaFilterLimitedValues(value as IRange<string>);
                                        field.onChange(limitedValues);
                                    }}
                                />
                            )}
                        />
                    </FieldWrapper>
                    {offerType === OfferType.FLAT && (
                        <FieldWrapper
                            labelContent={
                                <Text variant="body_copy_1" color="var(--colors-gray-700)" mb={1}>
                                    Piętra
                                </Text>
                            }
                            className={checkboxList}
                            dataTestId={getFilterAreaTestId("floor_choices")}
                        >
                            <FormProviderRHF {...formHook}>
                                <CheckboxControlled name="floorGround" labelContent="Parter" />
                                <CheckboxControlled name="floorGroundWithGarden" labelContent="Parter z ogródkiem" />
                                <CheckboxControlled name="floorOneToFour" labelContent="Niskie piętra (1-4)" />
                                <CheckboxControlled name="floorFivePlus" labelContent="Wysokie piętra (5+)" />
                                <CheckboxControlled name="floorLast" labelContent="Ostatnie piętro" />
                                <CheckboxControlled name="floorLastWithTerrace" labelContent="Ostatnie piętro z tarasem" />
                            </FormProviderRHF>
                        </FieldWrapper>
                    )}

                    {offerType === OfferType.HOUSE && (
                        <FieldWrapper
                            labelContent={
                                <Text variant="body_copy_1" color="var(--colors-gray-700)" mb={1}>
                                    Piętra
                                </Text>
                            }
                            className={checkboxList}
                            dataTestId={getFilterAreaTestId("house_storeys")}
                        >
                            <FormProviderRHF {...formHook}>
                                <CheckboxControlled name="floorGround" labelContent="Parterowy" />
                                <CheckboxControlled name="floorGroundWithGarden" labelContent="Piętrowy" />
                            </FormProviderRHF>
                        </FieldWrapper>
                    )}

                    <div className={modalFooter} data-testid={SEARCH_FILTERS_AND_SORT_TEST_ID.FOOTER_BUTTONS}>
                        <Button variant="filled_primary" size="small" fullWidth dataTestId={FILTERS_TEST_IDS.ACCEPT_BUTTON}>
                            Wybierz
                        </Button>

                        <Button type="button" variant="none_secondary" onClick={handlers.onCancel} size="small" dataTestId={FILTERS_TEST_IDS.CLEAN_BUTTON}>
                            Wyczyść
                        </Button>
                    </div>
                </form>
            </SystemModal.Content>
        </SystemModal>
    );
}

const flexStyle = css.raw({
    flex: 1,
    display: "flex",
    flexDirection: "column"
});

const modalContent = css({
    ...flexStyle,

    "&> div": {
        ...flexStyle
    }
});

const formStyle = css({
    ...flexStyle
});

const checkboxList = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 3,
    mb: 5
});

const modalFooter = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%"
});
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {FieldWrapper, IRange, RangeInput} from "@pg-design/inputs-module";
import {SystemModal} from "@pg-design/modal-module";
import {CheckboxControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {hitUserSegmentRooms} from "@pg-mono/google-tag-manager";
import {ControllerRHF, FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import {FILTERS_TEST_IDS, SEARCH_FILTERS_AND_SORT_TEST_ID} from "@pg-mono/test-ids";
import {OfferType} from "@pg-rp/common-types";

import {IOfferModalState} from "../../offer/types/OfferModalState";
import {errorMessages, hasAreaValidationError} from "../../real_estate/utils/has_area_validation_error";
import {SearchFiltersRooms} from "../../search/components/filters/SearchFiltersRooms";
import {getFilterAreaTestId} from "../../search/utils/filter_area_test_id";
import {getAreaFilterLimitedValues} from "../../search/utils/get_area_filter_proper_value";
import {useUserSearchPreferences} from "../../user/hooks/use_user_search_preferences";

interface IProps {
    offerType: OfferType;
    isOpen: boolean;
    filters?: IOfferModalState["filters"];
    handleClose: (values?: IFiltersFormValues) => void;
    handleConfirm: (values: IFiltersFormValues) => void;
}

export interface IFiltersFormValues {
    rooms: IOfferModalState["filters"]["rooms"];
    area: IOfferModalState["filters"]["area"];
    floorGround: boolean;
    floorGroundWithGarden: boolean;
    floorOneToFour: boolean;
    floorFivePlus: boolean;
    floorLast: boolean;
    floorLastWithTerrace: boolean;
}

const castToNumberIfExists = (value: string | undefined) => {
    return value ? Number.parseInt(value) : "";
};

export function PropertyListFiltersModal(props: IProps) {
    const {isOpen, filters, handleClose, handleConfirm, offerType} = props;
    const {userPreferences, saveUserPreferences} = useUserSearchPreferences();
    const userPreferencesFilters = userPreferences?.data?.filters || {};

    const formHook = useFormRHF<IFiltersFormValues>({
        defaultValues: {
            rooms: filters
                ? filters.rooms
                : {lower: castToNumberIfExists(userPreferencesFilters.rooms_0), upper: castToNumberIfExists(userPreferencesFilters.rooms_1)},
            area: filters ? filters.area : {lower: userPreferencesFilters.area_0 || "", upper: userPreferencesFilters.area_1 || ""},
            floorGround: filters ? Boolean(filters.floor_choices?.includes(1)) : Boolean(userPreferencesFilters.floor_choices?.includes("1")),
            floorGroundWithGarden: filters ? Boolean(filters.floor_choices?.includes(2)) : Boolean(userPreferencesFilters.floor_choices?.includes("2")),
            floorOneToFour: filters ? Boolean(filters.floor_choices?.includes(3)) : Boolean(userPreferencesFilters.floor_choices?.includes("3")),
            floorFivePlus: filters ? Boolean(filters.floor_choices?.includes(4)) : Boolean(userPreferencesFilters.floor_choices?.includes("4")),
            floorLast: filters ? Boolean(filters.floor_choices?.includes(5)) : Boolean(userPreferencesFilters.floor_choices?.includes("5")),
            floorLastWithTerrace: filters ? Boolean(filters.floor_choices?.includes(6)) : Boolean(userPreferencesFilters.floor_choices?.includes("6"))
        }
    });

    const {handleSubmit, control} = formHook;

    const handlers = {
        onSubmit: (data: IFiltersFormValues) => {
            if (hasAreaValidationError(data.area)) {
                return;
            }

            hitUserSegmentRooms(data.rooms);

            saveUserPreferences({
                filters: {
                    rooms_0: data.rooms.lower?.toString() || undefined,
                    rooms_1: data.rooms.upper?.toString() || undefined,
                    area_0: data.area.lower?.toString() || undefined,
                    area_1: data.area.upper?.toString() || undefined,
                    floor_choices: [
                        data.floorGround ? "1" : "",
                        data.floorGroundWithGarden ? "2" : "",
                        data.floorOneToFour ? "3" : "",
                        data.floorFivePlus ? "4" : "",
                        data.floorLast ? "5" : "",
                        data.floorLastWithTerrace ? "6" : ""
                    ].filter(Boolean)
                }
            });
            handleConfirm(data);
        },
        onCancel: () => {
            handleClose(formHook.getValues());
        }
    };

    const areaErrorMessage = hasAreaValidationError(formHook.watch("area")) ? errorMessages.area : undefined;

    return (
        <SystemModal isOpen={isOpen} variant="fit" headerVariant="gray" onModalClose={() => handleClose?.()}>
            <SystemModal.Header>
                <Text variant="body_copy_2">Filtry</Text>
            </SystemModal.Header>
            <SystemModal.Content className={modalContent}>
                <form onSubmit={handleSubmit(handlers.onSubmit)} className={formStyle}>
                    <FieldWrapper
                        labelContent={
                            <Text variant="body_copy_1" color="var(--colors-gray-700)" mb={1}>
                                Pokoje
                            </Text>
                        }
                        className={css({mb: 3})}
                        dataTestId={getFilterAreaTestId("rooms")}
                    >
                        <ControllerRHF
                            control={control}
                            name="rooms"
                            render={({field}) => (
                                <SearchFiltersRooms
                                    {...field}
                                    className={css({mb: 5})}
                                    toggleDropdown={() => null}
                                    clearField={() => null}
                                    showFooter={false}
                                    onChange={(_, value) => {
                                        field.onChange(value);
                                    }}
                                />
                            )}
                        />
                    </FieldWrapper>
                    <FieldWrapper
                        labelContent={
                            <Text variant="body_copy_1" color="var(--colors-gray-700)" mb={1}>
                                Metraż
                            </Text>
                        }
                        className={css({mb: 3})}
                        dataTestId={getFilterAreaTestId("area")}
                    >
                        <ControllerRHF
                            control={control}
                            name="area"
                            render={({field}) => (
                                <RangeInput
                                    {...field}
                                    error={areaErrorMessage}
                                    onChange={(_, value) => {
                                        const limitedValues = getAreaFilterLimitedValues(value as IRange<string>);
                                        field.onChange(limitedValues);
                                    }}
                                />
                            )}
                        />
                    </FieldWrapper>
                    {offerType === OfferType.FLAT && (
                        <FieldWrapper
                            labelContent={
                                <Text variant="body_copy_1" color="var(--colors-gray-700)" mb={1}>
                                    Piętra
                                </Text>
                            }
                            className={checkboxList}
                            dataTestId={getFilterAreaTestId("floor_choices")}
                        >
                            <FormProviderRHF {...formHook}>
                                <CheckboxControlled name="floorGround" labelContent="Parter" />
                                <CheckboxControlled name="floorGroundWithGarden" labelContent="Parter z ogródkiem" />
                                <CheckboxControlled name="floorOneToFour" labelContent="Niskie piętra (1-4)" />
                                <CheckboxControlled name="floorFivePlus" labelContent="Wysokie piętra (5+)" />
                                <CheckboxControlled name="floorLast" labelContent="Ostatnie piętro" />
                                <CheckboxControlled name="floorLastWithTerrace" labelContent="Ostatnie piętro z tarasem" />
                            </FormProviderRHF>
                        </FieldWrapper>
                    )}

                    {offerType === OfferType.HOUSE && (
                        <FieldWrapper
                            labelContent={
                                <Text variant="body_copy_1" color="var(--colors-gray-700)" mb={1}>
                                    Piętra
                                </Text>
                            }
                            className={checkboxList}
                            dataTestId={getFilterAreaTestId("house_storeys")}
                        >
                            <FormProviderRHF {...formHook}>
                                <CheckboxControlled name="floorGround" labelContent="Parterowy" />
                                <CheckboxControlled name="floorGroundWithGarden" labelContent="Piętrowy" />
                            </FormProviderRHF>
                        </FieldWrapper>
                    )}

                    <div className={modalFooter} data-testid={SEARCH_FILTERS_AND_SORT_TEST_ID.FOOTER_BUTTONS}>
                        <Button variant="filled_primary" size="small" fullWidth dataTestId={FILTERS_TEST_IDS.ACCEPT_BUTTON}>
                            Wybierz
                        </Button>

                        <Button type="button" variant="none_secondary" onClick={handlers.onCancel} size="small" dataTestId={FILTERS_TEST_IDS.CLEAN_BUTTON}>
                            Wyczyść
                        </Button>
                    </div>
                </form>
            </SystemModal.Content>
        </SystemModal>
    );
}

const flexStyle = css.raw({
    flex: 1,
    display: "flex",
    flexDirection: "column"
});

const modalContent = css({
    ...flexStyle,

    "&> div": {
        ...flexStyle
    }
});

const formStyle = css({
    ...flexStyle
});

const checkboxList = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 3,
    mb: 5
});

const modalFooter = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%"
});
